import React, { useState, useMemo } from 'react';
import { Subcontractor, Responsible, Task, StatementItem } from '../../../types';
import { KlFullModal } from '../modal/KlFullModal';
import { findTaskById, getTaskGanttRowNumber } from '../../../utils/planningUtils';

export interface MobileAddContractModalProps {
    open: boolean;
    onClose: () => void;
    currentForm: any;
    setCurrentForm: React.Dispatch<React.SetStateAction<any>>;
    isEditingAuthorized: boolean;
    subcontractors: Subcontractor[];
    responsibles?: Responsible[];
    globalUsers?: any[];
    tasks: Task[];
    onOpenTaskSelector: () => void;
    onOpenSubcontractorPicker: () => void;
    onOpenDocuments: () => void;
    onSave: () => void;
    approvalFlows?: any;
}

export const MobileAddContractModal: React.FC<MobileAddContractModalProps> = ({
    open,
    onClose,
    currentForm,
    setCurrentForm,
    isEditingAuthorized,
    subcontractors,
    responsibles,
    globalUsers,
    tasks,
    onOpenTaskSelector,
    onOpenSubcontractorPicker,
    onOpenDocuments,
    onSave,
    approvalFlows
}) => {
    const [partidaSearch, setPartidaSearch] = useState('');
    const [isAddingUser, setIsAddingUser] = useState(false);

    // Cálculos de montos y totales
    const totalAmount = useMemo(() => {
        return (currentForm.items || []).reduce((acc: number, i: StatementItem) => acc + (i.totalValue || 0), 0);
    }, [currentForm.items]);

    const isContractValid = Boolean(
        currentForm.subcontractorId &&
        currentForm.items?.length > 0 &&
        currentForm.rut &&
        currentForm.razonSocial &&
        currentForm.startDate &&
        currentForm.endDate &&
        currentForm.retention !== undefined &&
        ((currentForm as any).signedContractUrl || (currentForm.attachedDocuments && currentForm.attachedDocuments.length > 0))
    );

    const isDraft = Boolean(currentForm.subcontractorId || currentForm.items?.length > 0);

    // Filtrar partidas añadidas
    const filteredItems = useMemo(() => {
        const q = partidaSearch.trim().toLowerCase();
        if (!q) return currentForm.items || [];
        return (currentForm.items || []).filter((item: StatementItem) =>
            (item.code || '').toLowerCase().includes(q) ||
            (item.description || '').toLowerCase().includes(q)
        );
    }, [currentForm.items, partidaSearch]);

    // Eliminar una partida
    const handleRemoveItem = (itemId: string) => {
        if (isEditingAuthorized) return;
        setCurrentForm((prev: any) => ({
            ...prev,
            items: (prev.items || []).filter((i: StatementItem) => i.id !== itemId)
        }));
    };

    // Usuarios disponibles para vincular
    const availableUsers = useMemo(() => {
        const linked = new Set(currentForm.linkedUserIds || []);
        const list = responsibles || globalUsers || [];
        return list.filter((u: any) => !linked.has(u.id));
    }, [responsibles, globalUsers, currentForm.linkedUserIds]);

    const handleAddUser = (userId: string) => {
        if (!userId) return;
        setCurrentForm((prev: any) => ({
            ...prev,
            linkedUserIds: [...(prev.linkedUserIds || []), userId]
        }));
        setIsAddingUser(false);
    };

    const handleRemoveUser = (userId: string) => {
        if (isEditingAuthorized) return;
        setCurrentForm((prev: any) => ({
            ...prev,
            linkedUserIds: (prev.linkedUserIds || []).filter((id: string) => id !== userId)
        }));
    };

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title={currentForm.id ? 'Editar subcontrato' : 'Añadir subcontrato'}
            footer={
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-[0_0_36%] h-11 rounded-[12px] font-bold text-[14px] border border-[#CBD5E1] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={isEditingAuthorized || (!isContractValid && !isDraft)}
                        className={`flex-1 h-11 rounded-[12px] font-bold text-[14px] text-white transition-all active:scale-[0.98] ${
                            isEditingAuthorized || (!isContractValid && !isDraft)
                                ? 'bg-[#E2E8F0] dark:bg-slate-800 text-[#64748B] dark:text-slate-500 cursor-not-allowed'
                                : isContractValid
                                    ? 'bg-[#2563EB] hover:bg-blue-600 shadow-md'
                                    : 'bg-amber-500 hover:bg-amber-600 shadow-md'
                        }`}
                    >
                        {currentForm.id
                            ? 'Guardar Cambios'
                            : isContractValid
                                ? 'Habilitar contratista'
                                : 'Guardado Campos Ingresados'}
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-2.5 pb-6">
                {/* 1. DATOS DEL CONTRATISTA (06-anadir-subcontrato.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                        Datos del contratista
                    </div>

                    <div className="grid grid-cols-[1fr_1.5fr_auto] gap-2 items-end">
                        <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                                R.U.T
                            </span>
                            <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-slate-50 dark:bg-slate-800/60 px-3 flex items-center text-[13px] text-slate-900 dark:text-slate-100 overflow-hidden text-ellipsis whitespace-nowrap">
                                {currentForm.rut || <span className="text-slate-400">Sin RUT</span>}
                            </div>
                        </div>

                        <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                                Razón social
                            </span>
                            <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-slate-50 dark:bg-slate-800/60 px-3 flex items-center text-[13px] text-slate-900 dark:text-slate-100 overflow-hidden text-ellipsis whitespace-nowrap font-medium">
                                {currentForm.razonSocial || <span className="text-slate-400">Seleccionar...</span>}
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onOpenSubcontractorPicker}
                            disabled={isEditingAuthorized}
                            aria-label="Buscar razón social"
                            className="w-10 h-10 border border-[#BFDBFE] dark:border-blue-900/60 bg-[#EFF6FF] dark:bg-blue-950/60 text-[#1D4ED8] dark:text-blue-300 rounded-[10px] font-extrabold flex items-center justify-center shrink-0 active:scale-95 transition-transform disabled:opacity-50"
                        >
                            …
                        </button>
                    </div>
                </div>

                {/* 2. USUARIOS VINCULADOS (06-anadir-subcontrato.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                        <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                            Usuarios vinculados
                        </span>
                        {!isEditingAuthorized && availableUsers.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setIsAddingUser(!isAddingUser)}
                                className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                {isAddingUser ? 'Cerrar' : '+ Añadir'}
                            </button>
                        )}
                    </div>

                    <div className="min-h-[40px] border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800/40 p-1.5 flex flex-wrap gap-1.5 items-center">
                        {(currentForm.linkedUserIds || []).length === 0 ? (
                            <span className="text-slate-400 text-[12px] px-1.5 italic">
                                Ninguno seleccionado
                            </span>
                        ) : (
                            (currentForm.linkedUserIds || []).map((uid: string) => {
                                const u = (responsibles || []).find(r => r.id === uid) || (globalUsers || []).find(gu => gu.id === uid);
                                return (
                                    <span
                                        key={uid}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/40 text-[11px] font-medium"
                                    >
                                        <span>{u?.name || u?.email || uid}</span>
                                        {!isEditingAuthorized && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveUser(uid)}
                                                className="text-blue-400 hover:text-blue-700 ml-0.5"
                                            >
                                                <span className="material-symbols-outlined text-[14px]">close</span>
                                            </button>
                                        )}
                                    </span>
                                );
                            })
                        )}
                    </div>

                    {/* Selector desplegable de usuarios */}
                    {isAddingUser && availableUsers.length > 0 && (
                        <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 mt-1 max-h-40 overflow-y-auto space-y-1">
                            {availableUsers.map((u: any) => (
                                <div
                                    key={u.id}
                                    onClick={() => handleAddUser(u.id)}
                                    className="p-1.5 text-xs text-slate-800 dark:text-slate-200 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded cursor-pointer flex justify-between items-center"
                                >
                                    <span>{u.name || u.email}</span>
                                    <span className="text-[10px] text-slate-400 font-mono">{u.email}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="text-[11px] text-[#64748B] dark:text-slate-400 leading-snug">
                        Seleccione usuarios que participan en la obra para vincular al contratista.
                    </div>
                </div>

                {/* 3. PLAZO Y RETENCIÓN (06-anadir-subcontrato.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                        Plazo y retención
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                                F. Inicio
                            </span>
                            <div className="relative">
                                <input
                                    type="date"
                                    disabled={isEditingAuthorized}
                                    value={currentForm.startDate || ''}
                                    onChange={(e) => setCurrentForm({ ...currentForm, startDate: e.target.value })}
                                    className="w-full h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                                F. Término
                            </span>
                            <div className="relative">
                                <input
                                    type="date"
                                    disabled={isEditingAuthorized}
                                    value={currentForm.endDate || ''}
                                    onChange={(e) => setCurrentForm({ ...currentForm, endDate: e.target.value })}
                                    className="w-full h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-1/2 pr-1">
                        <div className="flex flex-col gap-1 min-w-0">
                            <span className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                                % Retención
                            </span>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                disabled={isEditingAuthorized}
                                value={currentForm.retention ?? 0}
                                onChange={(e) => setCurrentForm({ ...currentForm, retention: parseFloat(e.target.value) || 0 })}
                                className="w-full h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 text-[13px] text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 font-semibold"
                            />
                        </div>
                    </div>
                </div>

                {/* 4. PARTIDAS (06-anadir-subcontrato.html y 06b-anadir-subcontrato-con-partidas.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2.5">
                    <div className="flex items-center gap-2">
                        <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 flex-1 flex items-center gap-1.5">
                            <span>Partidas</span>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[10px] ${
                                (currentForm.items || []).length > 0
                                    ? 'bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-950 dark:text-blue-300'
                                    : 'bg-[#F1F5F9] text-[#475569] dark:bg-slate-800 dark:text-slate-400'
                            }`}>
                                {(currentForm.items || []).length}
                            </span>
                        </div>

                        <button
                            type="button"
                            onClick={onOpenTaskSelector}
                            disabled={isEditingAuthorized}
                            className="h-9 px-3 rounded-xl font-bold text-[13px] bg-[#EFF6FF] dark:bg-blue-950/80 border border-[#BFDBFE] dark:border-blue-900/60 text-[#1D4ED8] dark:text-blue-300 flex items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="M12 5v14" />
                            </svg>
                            <span>Añadir partidas</span>
                        </button>
                    </div>

                    {/* Si no hay partidas añadidas (06-anadir-subcontrato.html) */}
                    {(currentForm.items || []).length === 0 ? (
                        <div className="h-[84px] border-[1.5px] border-dashed border-[#CBD5E1] dark:border-slate-700 rounded-[12px] flex flex-col items-center justify-center gap-1.5 text-slate-500 dark:text-slate-400 text-[13px]">
                            <svg className="w-[22px] h-[22px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 5h.01M3 12h.01M3 19h.01M8 5h13M8 12h13M8 19h13" />
                            </svg>
                            <span>No hay partidas añadidas.</span>
                        </div>
                    ) : (
                        /* Si hay partidas añadidas (06b-anadir-subcontrato-con-partidas.html) */
                        <div className="flex flex-col gap-2.5">
                            {/* Buscador siempre visible con partidas según 06b */}
                            <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 flex items-center gap-2">
                                <svg className="w-[18px] h-[18px] text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.3-4.3" />
                                </svg>
                                <input
                                    type="text"
                                    value={partidaSearch}
                                    onChange={(e) => setPartidaSearch(e.target.value)}
                                    placeholder="Buscar partida…"
                                    className="w-full bg-transparent text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none"
                                />
                                {partidaSearch && (
                                    <button
                                        type="button"
                                        onClick={() => setPartidaSearch('')}
                                        className="text-slate-400 hover:text-slate-600 p-1"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">close</span>
                                    </button>
                                )}
                            </div>

                            {/* Tarjetas de partidas individuales (06b) */}
                            <div className="space-y-2">
                                {filteredItems.map((item: StatementItem) => {
                                    const masterTask = findTaskById(item.id, tasks);
                                    const ganttNum = masterTask ? getTaskGanttRowNumber(masterTask, tasks) : null;
                                    const displayCode = ganttNum !== null && ganttNum !== undefined
                                        ? `#${ganttNum}`
                                        : (item.code && String(item.code).trim() !== '' && String(item.code).trim() !== '-'
                                            ? (String(item.code).trim().startsWith('#') ? String(item.code).trim() : `#${String(item.code).trim()}`)
                                            : '-');
                                    const unitLabel = masterTask?.unit || (item.details?.[0]?.unitName ? '[unid.]' : '-');
                                    const totalQty = (item.details || []).reduce((s, d) => s + (parseFloat(d.quantity || '0') || 0), 0);

                                    return (
                                        <div
                                            key={item.id}
                                            className="border border-[#E2E8F0] dark:border-slate-700 rounded-[12px] p-2.5 flex flex-col gap-1.5 bg-white dark:bg-slate-800/90 shadow-xs"
                                        >
                                            <div className="flex gap-2 items-start">
                                                <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded-[10px] bg-[#F1F5F9] dark:bg-slate-700 text-[#64748B] dark:text-slate-300 shrink-0">
                                                    {displayCode}
                                                </span>
                                                <b className="flex-1 text-[13px] leading-snug text-[#0F172A] dark:text-slate-100">
                                                    {item.description}
                                                </b>
                                                {!isEditingAuthorized && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveItem(item.id)}
                                                        aria-label="Quitar partida"
                                                        className="w-6 h-6 text-[#94A3B8] hover:text-red-500 flex items-center justify-center shrink-0 active:scale-95"
                                                    >
                                                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 6 6 18" />
                                                            <path d="m6 6 12 12" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-100 dark:border-slate-700/60">
                                                <div>
                                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                                        Unidad
                                                    </div>
                                                    <div className="text-[13px] font-bold text-slate-800 dark:text-slate-200 mt-0.5 truncate">
                                                        {unitLabel}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                                        Total cant.
                                                    </div>
                                                    <div className="text-[13px] font-bold text-slate-800 dark:text-slate-200 mt-0.5">
                                                        {totalQty.toLocaleString('es-CL')}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                                        Valor acumulado
                                                    </div>
                                                    <div className="text-[13px] font-bold text-blue-700 dark:text-blue-300 font-mono mt-0.5 truncate">
                                                        ${(item.totalValue || 0).toLocaleString('es-CL')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Fila de Total Valor Acumulado (06b) */}
                            <div className="flex justify-between items-center text-[13px] pt-1 px-1">
                                <span className="text-[#64748B] dark:text-slate-400 font-medium">
                                    Total valor acumulado
                                </span>
                                <b className="text-[15px] font-extrabold text-[#0F172A] dark:text-slate-100 font-mono">
                                    ${totalAmount.toLocaleString('es-CL')}
                                </b>
                            </div>
                        </div>
                    )}
                </div>

                {/* 5. DOCUMENTOS DEL CONTRATO (06-anadir-subcontrato.html) */}
                <button
                    type="button"
                    onClick={onOpenDocuments}
                    className="w-full bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex items-center gap-2.5 text-left active:scale-[0.99] transition-transform"
                >
                    <span className="w-8 h-8 rounded-[10px] bg-[#FEF3C7] dark:bg-amber-950/60 text-[#B45309] dark:text-amber-300 flex items-center justify-center shrink-0">
                        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                    </span>
                    <b className="flex-1 text-[14px] text-[#0F172A] dark:text-slate-100">
                        Documentos del contrato
                    </b>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-[10px] ${
                        (currentForm.attachedDocuments || []).length > 0
                            ? 'bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-[#F1F5F9] text-[#475569] dark:bg-slate-800 dark:text-slate-400'
                    }`}>
                        {(currentForm.attachedDocuments || []).length}
                    </span>
                    <svg className="w-[18px] h-[18px] text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m9 18 6-6-6-6" />
                    </svg>
                </button>

                {/* 6. APROBACIONES (06-anadir-subcontrato.html) */}
                <div className="bg-white dark:bg-slate-900 rounded-[14px] p-3 shadow-xs border border-slate-100 dark:border-slate-800 flex flex-col gap-2">
                    <div className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100 flex items-center gap-2">
                        <svg className="w-[18px] h-[18px] text-[#2563EB]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                            <path d="m9 12 2 2 4-4" />
                        </svg>
                        <span>Aprobaciones</span>
                    </div>

                    {approvalFlows?.subcontracts && approvalFlows.subcontracts.length > 0 ? (
                        <div className="space-y-1.5 pt-1">
                            {approvalFlows.subcontracts.map((step: any, sIdx: number) => {
                                const isSigned = Boolean(currentForm.signatures?.[step.reviewerId]);
                                return (
                                    <div key={sIdx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 text-xs">
                                        <span className="font-medium text-slate-700 dark:text-slate-300">
                                            Paso {step.order}: {step.reviewerName || step.reviewerId}
                                        </span>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                            isSigned
                                                ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'
                                                : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                                        }`}>
                                            {isSigned ? 'Firmado' : 'Pendiente'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-[13px] text-[#64748B] dark:text-slate-400 italic">
                            No se han configurado firmas.
                        </div>
                    )}
                </div>
            </div>
        </KlFullModal>
    );
};
